import { DEFAULT_USE_IN_VIEW_OPTIONS } from "@/constants/tagular/main";
import { cn } from "@/lib/utils";
import { useMemo, useCallback, useState, useRef } from "react";
import { v4 as uuid } from "uuid";
import { useInView } from "react-intersection-observer";
import type { ProductCompareMonarchProps } from "@/monarch/types/ProductCompare";
import useProductCompareData from "./useProductCompareData";
import { buildCompareProductEvents } from "./useCompareProductEvents";
import ProductCompareLoading from "./components/ProductCompareLoading";
import { STICKY_OFFSET_ALL, STICKY_OFFSET_MOBILE } from "./constants";
import { useProductCompareMessages } from "@/i18n/hooks/useProductCompareMessages";
import {
  buildRowDefs,
  normalizeProductUuids,
  mapProductsToComparisonItems,
  filterEmptyRows,
} from "./utils";
import {
  ComparisonTable,
  type ComparisonTableApi,
  type ComparisonRowDef,
  CarouselPrevious,
  CarouselNext,
  CarouselDots,
  Flex,
} from "@highereducation/hen-ui";

export { buildRowDefs, normalizeProductUuids } from "./utils";

export interface ProductCompareProps {
  productCompare?: ProductCompareMonarchProps;
  productUuids?: string[] | null;
  containerClassName?: string;
  currentCourseCheckoutUrl?: string | null;
  location?: string | null;
  currentCtaLabel?: string;
  /**
   * "all" — offset at all breakpoints (program pages, banner always visible).
   * "mobile-only" — offset only below lg (course HeroSideRail, banner only visible when side rail scrolls away).
   */
  hasStickyOffset?: "all" | "mobile-only";
}

export default function ProductCompare({
  productCompare,
  containerClassName = "",
  productUuids,
  currentCourseCheckoutUrl,
  location,
  hasStickyOffset,
  currentCtaLabel,
}: ProductCompareProps) {
  const [api, setApi] = useState<ComparisonTableApi | null>(null);
  const messages = useProductCompareMessages();

  const { ref: contentRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  const normalizedProductUuids = normalizeProductUuids(productUuids);
  const currentProductUuid = normalizedProductUuids?.[0];
  const { isLoading, products } = useProductCompareData(
    normalizedProductUuids && normalizedProductUuids.length >= 2
      ? normalizedProductUuids
      : null,
  );

  // Stable correlationId per product, persisted across re-renders.
  // Using a ref ensures the same UUID survives useMemo recomputation (e.g. when
  // products changes reference), so onClicked and onCtaClicked always share the
  // same ID and the cookie written at click time matches what the PDP reads.
  const correlationIdsRef = useRef<Record<string, string>>({});
  const getStableCorrelationIds = (): Record<string, string> => {
    (products ?? []).forEach(({ productUuid }) => {
      if (!correlationIdsRef.current[productUuid]) {
        correlationIdsRef.current[productUuid] = uuid();
      }
    });
    return correlationIdsRef.current;
  };

  // Per-product ProductViewed / ProductClicked fire functions, keyed by uuid.
  // Memoised so the object reference (and therefore the callbacks passed to
  // hen-ui) only changes when products or location actually change — preventing
  // useColumnViewObserver from recreating its IntersectionObserver on every render.
  const compareEvents = useMemo(
    () =>
      buildCompareProductEvents(
        products ?? [],
        location ?? "product-detail-page",
        getStableCorrelationIds(),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [products, location],
  );

  const handleProductViewed = useCallback(
    (id: string) => compareEvents[id]?.onViewed(),
    [compareEvents],
  );
  const handleProductClicked = useCallback(
    (id: string) => {
      // The first item fires ConversionTracked on CTA click instead — skip ProductClicked for it.
      if (id === products?.[0]?.productUuid) return;
      compareEvents[id]?.onClicked();
    },
    [compareEvents, products],
  );
  const handleCtaClick = useCallback(
    (id: string, cta: { label: string; href: string }, loc: "top" | "bottom") =>
      compareEvents[id]?.onCtaClicked(cta, loc),
    [compareEvents],
  );

  if (isLoading) {
    return (
      <ProductCompareLoading
        isLoading={isLoading}
        containerClassName={containerClassName}
      />
    );
  }

  if (!productCompare || !products || products.length < 2) return null;

  const {
    header,
    subheader,
    topCta = false,
    bottomCta = true,
    ctaLabel,
    schoolNameAsTitle,
    showBundleBadge = true,
    showPriceStrike = true,
    dataPoints,
  } = productCompare;

  const items = mapProductsToComparisonItems(products, {
    currentProductUuid,
    currentCourseCheckoutUrl,
    ctaLabel,
    currentCtaLabel,
    schoolNameAsTitle,
    topCta,
    bottomCta,
    showBundleBadge,
    showPriceStrike,
    messages,
  });

  // If Monarch provides dataPoints, buildRowDefs will filter and reorder rows to match that config.
  // If no dataPoints are provided via Monarch, all rows will be included by default.

  const rowDefs: ComparisonRowDef[] = buildRowDefs(
    messages.defaultRowDefs,
    dataPoints,
  );
  const filteredRowDefs = filterEmptyRows(rowDefs, items, messages.unavailable);
  if (filteredRowDefs.length === 0) return null;

  return (
    <div
      ref={contentRef}
      className={cn("flex flex-col gap-6", containerClassName)}
    >
      <div className="flex flex-col">
        <h2 className="my-6 text-3xl font-bold leading-tight">
          {header ?? messages.defaultHeader}
        </h2>
        <p className="text-lg text-gray-600 mb-0">
          {subheader ?? messages.defaultSubheader}
        </p>
      </div>

      <div
        data-hen-ui
        className={cn(
          "flex flex-col gap-4",
          hasStickyOffset === "all" && STICKY_OFFSET_ALL,
          hasStickyOffset === "mobile-only" && STICKY_OFFSET_MOBILE,
        )}
      >
        <ComparisonTable
          items={items}
          rowDefs={filteredRowDefs}
          maxItems={4}
          visibleItems={3}
          setApi={setApi}
          styleVersion="v1"
          inView={inView}
          onProductViewed={handleProductViewed}
          onProductClicked={handleProductClicked}
          onCtaClick={handleCtaClick}
        />
        {items.length > 2 && (
          <Flex
            direction="row"
            align="center"
            justify="center"
            gap="sm"
            className="hidden md:flex"
            role="group"
            aria-label={messages.tableNavAriaLabel}
          >
            <CarouselPrevious
              disabled={!api?.canScrollPrev}
              aria-disabled={!api?.canScrollPrev}
              onClick={() => api?.scrollPrev()}
              aria-label={messages.previousColumnAriaLabel}
              design="outline"
            />
            {api && (
              <CarouselDots
                count={api.totalPages}
                activeIndex={api.currentPage}
                onDotClick={(i) => api.scrollToPage(i)}
                // Keep hen-ui default role="group" — role="tablist" is invalid
                // here because the dots are plain buttons, not role="tab".
                aria-label={messages.selectPageAriaLabel}
                design="outline"
              />
            )}
            <CarouselNext
              disabled={!api?.canScrollNext}
              aria-disabled={!api?.canScrollNext}
              onClick={() => api?.scrollNext()}
              aria-label={messages.nextColumnAriaLabel}
              design="outline"
            />
          </Flex>
        )}
      </div>
    </div>
  );
}
